import axios from 'axios';
import jwt_decode from "jwt-decode";
import { useContext, useRef } from 'react';
import AuthContext from "../contexts/AuthContext";
import { reportBackendUnavailable } from "./backendUnavailableBus";
import { clearPrefetchCache } from "./tabPrefetch";
import { attachVisitorVerification } from "./visitorVerification";

const baseURL = window.location.origin;

let refreshPromise = null;

// Short-lived GET de-dup cache - same idea, same TTL, and the same
// mutation-clears-it rule as tabPrefetch.js's own cache (which only covers
// its own explicit prefetch calls), generalized here to every GET made
// through any useAxios() instance. Several sibling components independently
// firing the exact same request on mount (common - each fetches its own
// slice but several happen to want the same endpoint, e.g. the tournament
// itself) used to mean N near-simultaneous network round-trips for data
// that's about to be identical; now the first one wins and the rest share
// its promise.
// Deliberately NOT a real cache: the 2s TTL only catches requests that are
// genuinely concurrent, never serves data old enough for a user to notice -
// this is not the mechanism for "revisiting a tournament shows it instantly
// without refetching". A longer TTL would risk showing stale scores/brackets
// after someone else's edit, undermining the live-update (socket push) and
// race-guard (reqRef counters) work already done throughout the draws/
// groups/schedule pages - not worth it for what's fundamentally a
// duplicate-request optimization, not a data cache.
const GET_DEDUP_TTL = 2000;
const getDedupCache = window.__axiosGetDedupCache || (window.__axiosGetDedupCache = new Map());

const buildGetCacheKey = (url, params) => `${url}?${params ? JSON.stringify(params) : ''}`;

const useAxios = () => {
    // Read fresh every render (so a real auth change - login, token refresh,
    // logout - is never missed), but stashed in a ref rather than closed
    // over directly by the interceptors below, which are only ever set up
    // once per component instance (see instanceRef below).
    const authRef = useRef();
    authRef.current = useContext(AuthContext);

    // One axios instance per component instance, not one per render. This
    // used to be axios.create() fresh on every render - harmless on its own,
    // but every useCallback/useMemo anywhere in the app that (correctly)
    // lists `api` in its own deps array inherited that same per-render churn,
    // which in turn defeated React.memo on every component those callbacks
    // get passed down into as props (Bracket.js/BracketClassic.js/
    // TournamentGroupTable.js/ScheduleTable.js - shallow prop comparison
    // sees "changed" every time regardless of anything else being stable).
    // That showed up as switching stages / socket score pushes re-rendering
    // every mounted bracket/group table in full *plus* paying for the now-
    // pointless memo comparison on top - worse than before memo existed.
    const instanceRef = useRef(null);
    if (!instanceRef.current) {
        const axiosInstance = axios.create({
            baseURL,
        });

        axiosInstance.interceptors.request.use(req => {
            const storedTokensStr = localStorage.getItem('authTokens');
            if (storedTokensStr) {
                const tokens = JSON.parse(storedTokensStr);
                req.headers.Authorization = `Bearer ${tokens.access}`;
            }
            const method = req.method?.toLowerCase();
            if (method === "post" || method === "put" || method === "patch" || method === "delete") {
                clearPrefetchCache();
                getDedupCache.clear();
            }
            return req;
        });

        axiosInstance.interceptors.response.use(
            (response) => {
                const method = response.config?.method?.toLowerCase();
                if (method === "post" || method === "put" || method === "patch" || method === "delete") {
                    clearPrefetchCache();
                    getDedupCache.clear();
                }
                return response;
            },
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    const storedTokensStr = localStorage.getItem('authTokens');
                    if (!storedTokensStr) return Promise.reject(error);

                    const latestTokens = JSON.parse(storedTokensStr);

                    if (!refreshPromise) {
                        refreshPromise = axios.post(`${baseURL}/api/v1.0/token/refresh/`, {
                            refresh: latestTokens.refresh
                        }).then(response => {
                            const newTokens = { ...latestTokens, ...response.data };

                            authRef.current.setAuthTokens(newTokens);
                            authRef.current.setUser(jwt_decode(newTokens.access));
                            localStorage.setItem('authTokens', JSON.stringify(newTokens));

                            return newTokens;
                        }).catch(err => {
                            console.error("Refresh token dead", err);
                            if (authRef.current.logoutUser) authRef.current.logoutUser();
                            throw err;
                        }).finally(() => {
                            refreshPromise = null;
                        });
                    }

                    try {
                        const data = await refreshPromise;

                        originalRequest.headers.Authorization = `Bearer ${data.access}`;
                        return axiosInstance(originalRequest);
                    } catch (e) {
                        return Promise.reject(e);
                    }
                }

                // 501 only, and GET only: 404 is unusable as this signal - DRF returns
                // it just as often for "that specific id doesn't exist" (a normal,
                // page-local lookup failure, e.g. a stale club_id in the URL) as for
                // "this endpoint was never built", and there's no way to tell those
                // apart here. 501 Not Implemented doesn't have that ambiguity - a
                // backend only sends it to deliberately mean "not built yet".
                if (error.response?.status === 501 && originalRequest?.method === "get") {
                    reportBackendUnavailable(window.location.pathname);
                }

                return Promise.reject(error);
            }
        );

        // Wraps (not replaces) the instance's own .get - every existing
        // api.get(url, config) call site keeps working unchanged. Skipped
        // whenever config carries anything beyond params (custom headers,
        // responseType, etc.) - those signal a request whose response depends
        // on more than just the URL+params key below, which a shared cached
        // promise can't safely account for.
        const originalGet = axiosInstance.get.bind(axiosInstance);
        axiosInstance.get = (url, config) => {
            const extraKeys = config ? Object.keys(config).filter(k => k !== 'params') : [];
            if (extraKeys.length > 0) return originalGet(url, config);

            const key = buildGetCacheKey(url, config?.params);
            const cached = getDedupCache.get(key);
            if (cached && Date.now() - cached.ts < GET_DEDUP_TTL) return cached.promise;

            const promise = originalGet(url, config).catch(err => {
                getDedupCache.delete(key);
                throw err;
            });
            getDedupCache.set(key, { promise, ts: Date.now() });
            return promise;
        };

        attachVisitorVerification(axiosInstance);

        instanceRef.current = axiosInstance;
    }

    return instanceRef.current;
};

export default useAxios;
